import { useState } from "react";
import { Copy, Pencil, Trash2, Plus, Info, Tag, MapPin, Plane, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/api";
import { STATUS, activePromos, groupByMonth, monthLabel, fmtISO } from "@/lib/seat";
import { hasPrice, fmtRp } from "@/lib/price";

const DepChip = ({ d, onClick }) => (
  <button onClick={() => onClick(d)} title={[STATUS[d.status].label, hasPrice(d.price) && `Total ${fmtRp(d.price.total)}`, d.remark, d.updated_by && `oleh ${d.updated_by}`].filter(Boolean).join(" \u2022 ")}
    data-testid={`seat-departure-chip-${d.id}`}
    className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium tabular-nums transition-transform hover:-translate-y-0.5 ${STATUS[d.status].cls}`}>
    {d.date.slice(8, 10)}
    {d.operator && <span className="text-[10px] opacity-70">{d.operator}</span>}
    {d.status !== "OPEN" && <span className="text-[10px] font-semibold">{STATUS[d.status].short}</span>}
    {d.remark && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
  </button>
);

const CopyBtn = ({ text, label, testid }) => (
  <button data-testid={testid} onClick={async () => ((await copyText(text)) ? toast.success(`${label} disalin`) : toast.error("Gagal menyalin"))}
    className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-dark transition-colors">
    <Copy className="h-3.5 w-3.5" /> {label}
  </button>
);

export const ProductCard = ({ p, isAdmin, index, onDetail, onEdit, onDelete, onAddDep, onEditDep }) => {
  const [showAll, setShowAll] = useState(false);
  const promos = activePromos(p);
  const months = Object.entries(groupByMonth(p.shown));
  const visible = showAll ? months : months.slice(0, 3);
  return (
    <article data-testid="seat-product-card" style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
      className="fade-up rounded-lg border border-slate-200 bg-white p-5 transition-[box-shadow,border-color] hover:border-slate-300 hover:shadow-md">
      <div className="flex flex-col gap-3">
        <div className="min-w-0">
          <p className="overline-label">{p.group || "Tanpa grup"}</p>
          <h3 className="font-heading mt-1 text-lg font-semibold tracking-tight text-slate-900" data-testid="seat-product-name">
            {p.name} {p.days && <span className="ml-1 rounded bg-sky-50 px-1.5 py-0.5 text-xs font-medium text-sky-700">{p.days}</span>}
          </h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            {p.departure_city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.departure_city}</span>}
            {p.airline && <span className="inline-flex items-center gap-1"><Plane className="h-3.5 w-3.5" />{p.airline}</span>}
            {p.operator && <span>Operator: {p.operator}</span>}
            {p.template_ids?.length > 0 && <span className="inline-flex items-center gap-1 text-brand"><MessageSquareText className="h-3.5 w-3.5" />{p.template_ids.length} balasan cepat</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {p.itinerary_regular && <CopyBtn text={p.itinerary_regular} label="Salin Reguler" testid="seat-copy-regular" />}
          {p.itinerary_promo && <CopyBtn text={p.itinerary_promo} label="Salin Promo" testid="seat-copy-promo" />}
          <button onClick={() => onDetail(p)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Detail" data-testid="seat-detail-button"><Info className="h-4 w-4" /></button>
          {isAdmin && <button onClick={() => onEdit(p)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit" data-testid="seat-edit-product-button"><Pencil className="h-4 w-4" /></button>}
          {isAdmin && <button onClick={() => onDelete(p)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label="Hapus" data-testid="seat-delete-product-button"><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      {promos.map((x, i) => (
        <p key={i} className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="seat-active-promo">
          <Tag className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span><b>{x.note}</b>{(x.start || x.end) && <span className="ml-1 text-amber-700">({x.start ? fmtISO(x.start) : "..."} s/d {x.end ? fmtISO(x.end) : "..."})</span>}</span>
        </p>
      ))}
      <div className="mt-4 space-y-2.5">
        {months.length === 0 && <p className="text-xs text-slate-400">Belum ada tanggal keberangkatan.</p>}
        {visible.map(([key, deps]) => (
          <div key={key} className="flex flex-col gap-1.5 sm:flex-row sm:items-start">
            <span className="w-32 shrink-0 pt-1 text-xs font-medium text-slate-500">{monthLabel(key)}</span>
            <div className="flex flex-wrap gap-1.5">{deps.map((d) => <DepChip key={d.id} d={d} onClick={onEditDep} />)}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-4">
        <button onClick={() => onAddDep(p)} className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline" data-testid="seat-add-departure-button"><Plus className="h-3.5 w-3.5" /> Tambah tanggal</button>
        {months.length > 3 && <button onClick={() => setShowAll(!showAll)} className="text-xs text-slate-500 hover:text-slate-900" data-testid="seat-toggle-months">{showAll ? "Ringkas" : `Lihat ${months.length - 3} bulan lagi`}</button>}
      </div>
    </article>
  );
};
